// Team invoices, and /finance's Invoices list of both kinds (finance-real, the user 2026-10-22), read and written as
// useLineJobs' are: the reads sit under financeKeys.all, so useFinanceMutation refetches them after every write, a claim's
// job and its client's page too. Each read and write calls teamInvoiceService.
import { useQuery } from "@tanstack/react-query";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import type { TeamInvoice, TeamInvoiceClaimInput, TeamInvoiceCreateInput, TeamInvoiceNewJobInput, TeamInvoicePayoutInput, TeamInvoiceStatusInput } from "../types";
import { claimsCount } from "../utils/names";
import { financeKeys, useFinanceMutation } from "./financeQuery";
import { teamInvoiceService } from "../services/teamInvoiceService";

const teamWord = ENTITY_CONFIG.teamInvoice.label.toLowerCase();
const claimsText = (i: TeamInvoice): string => claimsCount(i.claims.length);

export const teamInvoiceKeys = {
  list: () => [...financeKeys.all, "agency-invoice-list"] as const,
  detail: (id: string) => [...financeKeys.all, "team-invoice", id] as const,
  candidates: (personId: string) => [...financeKeys.all, "team-invoice-candidates", personId] as const,
  total: (personId: string, claimIds: string[]) => [...financeKeys.all, "team-claims-total", personId, ...claimIds] as const,
};

/** /finance's Invoices: client invoices and team invoices in one list. */
export const useAgencyInvoiceList = (enabled = true) => useQuery({ queryKey: teamInvoiceKeys.list(), queryFn: teamInvoiceService.list, enabled });

export const useTeamInvoice = (id: string | null) =>
  useQuery({ queryKey: teamInvoiceKeys.detail(id ?? "none"), queryFn: () => teamInvoiceService.detail(id ?? ""), enabled: !!id });

/** New team invoice's list for the person picked: their draft, if any, their owed claims and the jobs to claim on. */
export const useTeamInvoiceCandidates = (personId: string | null) =>
  useQuery({ queryKey: teamInvoiceKeys.candidates(personId ?? "none"), queryFn: () => teamInvoiceService.candidates(personId ?? ""), enabled: !!personId });

/** Where teamInvoiceKeys.total puts the person. */
const TOTAL_PERSON_AT = teamInvoiceKeys.total("", []).length - 1;

/** What the claims ticked add up to, as the server sums them. Nothing ticked asks nothing and reads 0; otherwise the
 * same person's last sum stays on screen while a new tick's loads, so it never blanks, and another person's never shows. */
export const useTeamClaimsTotal = (personId: string | null, claimIds: string[]) => {
  const person = personId ?? "none";
  return useQuery({
    queryKey: teamInvoiceKeys.total(person, claimIds),
    queryFn: () => teamInvoiceService.claimsTotal(personId ?? "", claimIds).then((t) => t.totalMinor),
    enabled: !!personId && claimIds.length > 0,
    placeholderData: (previous, previousQuery) => (claimIds.length === 0 ? 0 : previousQuery?.queryKey[TOTAL_PERSON_AT] === person ? previous : undefined),
  });
};

export const useCreateTeamInvoice = () =>
  useFinanceMutation((input: TeamInvoiceCreateInput) => teamInvoiceService.create(input), (i) => `A draft ${teamWord} from ${i.person.name}, with ${claimsText(i)}.`);

/** The Team tab's Pay: their draft, or a new one. No toast: the drawer it opens says what it holds. */
export const useOpenTeamInvoice = () => useFinanceMutation((personId: string) => teamInvoiceService.open(personId));

export const useAddTeamInvoiceClaims = () =>
  useFinanceMutation((v: { invoiceId: string; claimIds: string[] }) => teamInvoiceService.addClaims(v.invoiceId, v.claimIds), (i) => `On the ${teamWord}: ${claimsText(i)}.`);

export const useRemoveTeamInvoiceClaim = () =>
  useFinanceMutation((v: { invoiceId: string; claimId: string }) => teamInvoiceService.removeClaim(v.invoiceId, v.claimId), () => `Off the ${teamWord}: the claim stays on its job.`);

/** A refusal (more than is free) shows beside the amount: `refusedInline`. */
export const useClaimOnTeamInvoice = () =>
  useFinanceMutation((v: { invoiceId: string; input: TeamInvoiceClaimInput }) => teamInvoiceService.claim(v.invoiceId, v.input), (i) => `Claimed, on the ${teamWord} from ${i.person.name}.`, {
    refusedInline: true,
  });

export const useNewJobOnTeamInvoice = () =>
  useFinanceMutation(
    (v: { invoiceId: string; input: TeamInvoiceNewJobInput }) => teamInvoiceService.newJob(v.invoiceId, v.input),
    (i) => `The new ${ENTITY_CONFIG.contract.label.toLowerCase()} is active, with the claim on it, on the ${teamWord} from ${i.person.name}.`,
    { refusedInline: true },
  );

export const useSetTeamInvoiceStatus = () =>
  useFinanceMutation(
    (v: { invoiceId: string; input: TeamInvoiceStatusInput }) => teamInvoiceService.setStatus(v.invoiceId, v.input),
    (i) => (i.status === "received" ? `${i.person.name} · ${i.title} is received.` : `The ${teamWord} from ${i.person.name} is a draft again.`),
  );

/** Paid: the payout. Its claims are locked from then. */
export const usePayTeamInvoice = () =>
  useFinanceMutation((v: { invoiceId: string; input: TeamInvoicePayoutInput }) => teamInvoiceService.payout(v.invoiceId, v.input), (i) => `${i.person.name} · ${i.title}: its claims are paid out.`);
